import React, { useState } from 'react';
import Checkbox, { CheckboxLarge, CheckboxMedium } from '@ajat/design-system/Checkbox';
import { ComponentPage, DemoCard, Demo } from '../shared';

export const meta = {
  component: Checkbox,
  componentName: 'Checkbox',
  title: 'Checkbox',
  group: 'Forms',
  importPath: '@ajat/design-system/Checkbox',
  figmaNode: '4109:324',
  description:
    'Multi-select control in two sizes. Large has a 32px square with a success-green fill and an optional description; Medium has a 24px square and an uppercase title, used for dense option lists like the create-school print colours.',
  props: [
    ['size', "'large' | 'medium'", "'medium'", 'Selector size and title treatment'],
    ['title', 'string', '—', 'Primary label'],
    ['description', 'string', 'null', 'Secondary line under the title'],
    ['checked', 'bool', 'false', 'Checked state'],
    ['disabled', 'bool', 'false', 'Disables the control'],
    ['error', 'string', "''", 'Validation message under the box — red selector, aria-invalid + aria-describedby'],
    ['name', 'string', '—', 'Native name'],
    ['value', 'string', '—', 'Native value'],
    ['onChange', 'func', '—', 'Fires when the box is toggled'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  propsNote:
    'CheckboxLarge and CheckboxMedium are pass-through wrappers that pin size — they take exactly these props minus size.',
  usage: [
    {
      title: 'A capped multi-select',
      code: `import { CheckboxMedium } from '@ajat/design-system/Checkbox';

// Create-school print colours: at most two ticks, so the
// remaining boxes disable once the cap is reached.
const MAX = 2;

{COLOURS.map((c) => {
  const checked = selected.includes(c.id);
  return (
    <CheckboxMedium
      key={c.id}
      title={c.label}
      checked={checked}
      disabled={!checked && selected.length >= MAX}
      onChange={() => toggle(c.id)}
    />
  );
})}`,
    },
  ],
};

const LARGE_OPTIONS = [
  { id: 'option-1', title: 'Newsletter', description: 'Occasional updates by e-mail.' },
  { id: 'option-2', title: 'SMS updates', description: 'Delivery notifications by text.' },
];

const MEDIUM_OPTIONS = [
  { id: 'white', title: 'Hvidt tryk' },
  { id: 'black', title: 'Sort tryk' },
  { id: 'other', title: 'Anden farve' },
];

const MAX_MEDIUM = 2;

export default function CheckboxPage() {
  const [large, setLarge] = useState(['option-1']);
  const [medium, setMedium] = useState(['white']);

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((v) => v !== id) : [...list, id]);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Large" hint="32px selector · success-green fill">
        <div className="flex flex-col gap-[var(--space-ui-medium-l)]">
          <Demo caption="DEFAULT"><CheckboxLarge title="Accept terms" /></Demo>
          <Demo caption="HOVER"><CheckboxLarge className="ds-checkbox--preview-hover" title="Accept terms" /></Demo>
          <Demo caption="SELECTED"><CheckboxLarge title="Accept terms" checked /></Demo>
          <Demo caption="DISABLED"><CheckboxLarge title="Accept terms" disabled /></Demo>
          <Demo caption="WITH DESCRIPTION">
            <CheckboxLarge
              title="Subscribe to newsletter"
              description="Occasional updates about new collections and offers."
              checked
            />
          </Demo>
        </div>
      </DemoCard>

      <DemoCard title="Medium" hint="24px selector · uppercase title">
        <div className="flex flex-col gap-[var(--space-ui-medium-l)]">
          <Demo caption="DEFAULT"><CheckboxMedium title="Hvidt tryk" /></Demo>
          <Demo caption="HOVER"><CheckboxMedium className="ds-checkbox--preview-hover" title="Hvidt tryk" /></Demo>
          <Demo caption="SELECTED"><CheckboxMedium title="Hvidt tryk" checked /></Demo>
          <Demo caption="DISABLED"><CheckboxMedium title="Hvidt tryk" disabled /></Demo>
          <Demo caption="WITH DESCRIPTION">
            <CheckboxMedium title="Rygtryk" description="Large print on the back." checked />
          </Demo>
        </div>
      </DemoCard>

      <DemoCard title="Interactive group — large">
        <div className="flex flex-col gap-[var(--space-ui-small-l)]">
          {LARGE_OPTIONS.map((o) => (
            <CheckboxLarge
              key={o.id}
              name={o.id}
              title={o.title}
              description={o.description}
              checked={large.includes(o.id)}
              onChange={() => toggle(large, setLarge, o.id)}
            />
          ))}
        </div>
      </DemoCard>

      <DemoCard title="Capped group — medium" hint={`max ${MAX_MEDIUM} — the rest disable`}>
        <div className="flex flex-col gap-[var(--space-ui-small-l)]">
          {MEDIUM_OPTIONS.map((o) => {
            const checked = medium.includes(o.id);
            return (
              <CheckboxMedium
                key={o.id}
                name={o.id}
                title={o.title}
                checked={checked}
                disabled={!checked && medium.length >= MAX_MEDIUM}
                onChange={() => toggle(medium, setMedium, o.id)}
              />
            );
          })}
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
